// SEO B2.4 — cost guides / how-to blog. High-volume informational searches ("how much does a handyman
// cost", "tv mounting cost") + strong AI-search (ChatGPT/Perplexity cite cost guides). Honest, useful,
// unique content. Prices are typical 2026 U.S. ranges for guidance only — actual prices vary by city,
// job and pro. Each key here is live at /guides/<key>. Add a key → it's a new indexable page.

// Home Care pillar (maintenance & prevention) lives in its own file and merges below,
// so every article — cost guide or home-care — is served by the same /guides/:slug route.
import { HOME_CARE_PAGES } from './homeCareData';

const CTA = '/category?type=all';

export const GUIDE_PAGES = {
  'handyman-cost': {
    service: 'Handyman',
    serviceSlug: 'handyman',
    category: 'Cost guide',
    title: 'How Much Does a Handyman Cost? (2026 Price Guide) | BestHand',
    metaDescription:
      'How much does a handyman cost in 2026? Typical handyman rates are $50–$125/hour or $200–$600 for most jobs. See a full price breakdown by task, plus tips to save.',
    h1: 'How much does a handyman cost?',
    updated: 'June 2026',
    intro:
      'A handyman is one of the most cost-effective ways to handle small home repairs and odd jobs. In 2026, most U.S. handymen charge $50–$125 per hour, and a typical job runs $200–$600 depending on the work, your location and the materials needed. Below is a clear breakdown so you know what to expect before you book.',
    costTable: {
      caption: 'Typical handyman costs (2026, U.S.)',
      head: ['How charged', 'Typical range', 'Notes'],
      rows: [
        ['Hourly rate', '$50–$125 / hour', 'Most common; minimum 1–2 hours'],
        ['Half-day (up to 4 hrs)', '$200–$400', 'Multiple small jobs at once'],
        ['Full day (up to 8 hrs)', '$400–$800', 'Best value for a punch-list'],
        ['Small single task', '$75–$200', 'TV mount, faucet swap, shelf install'],
        ['Service call / minimum', '$50–$100', 'Some pros charge a trip minimum'],
      ],
    },
    sections: [
      {
        h2: 'What affects the price',
        body: [
          'Three things drive a handyman quote the most: how long the job takes, how specialized it is, and your location. Labor in major metros (New York, San Francisco, Boston) tends to run 20–40% above the national average, while smaller cities sit below it.',
          'Materials are usually billed on top of labor. For a transparent booking, list every task up front — bundling several small jobs into one visit almost always lowers your effective hourly cost because you only pay one trip charge.',
        ],
      },
      {
        h2: 'Hourly vs. flat-rate pricing',
        body: [
          'Hourly pricing is best for unpredictable or mixed work (a punch-list of small repairs). Flat-rate pricing is better for well-defined single tasks like mounting a TV or assembling furniture, where the pro can quote a fixed price up front.',
          'On BestHand you see upfront pricing before you book and pay securely in the app when the job is done — no surprise quotes at the end.',
        ],
      },
      {
        h2: 'How to save on handyman work',
        body: [
          // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
          'Bundle tasks into one visit, buy your own materials when you can, be ready and clear about the scope, and book mid-week when pros have more availability. Avoid the cheapest unvetted quote — a background-checked, well-reviewed pro who does it right the first time is cheaper than a redo.',
        ],
      },
    ],
    faqs: [
      ['How much does a handyman charge per hour in 2026?', 'Most U.S. handymen charge $50–$125 per hour, with higher rates in major metro areas. Many have a 1–2 hour minimum.'],
      ['Is it cheaper to hire a handyman or a specialist?', 'For small, general repairs a handyman is usually cheaper. For licensed work like major plumbing, electrical or HVAC, hire the specialized trade.'],
      ['Do handymen charge for materials separately?', 'Yes, materials are typically billed on top of labor. You can often save by buying the parts yourself.'],
      ['What is the minimum charge for a handyman?', 'Many pros have a minimum of one to two hours or a $50–$100 trip charge, so bundling small jobs into one visit is the best value.'],
    ],
    related: ['tv-mounting-cost', 'furniture-assembly-cost', 'how-to-hire-a-handyman'],
  },

  'tv-mounting-cost': {
    service: 'TV Mounting',
    serviceSlug: 'tv-mounting',
    category: 'Cost guide',
    title: 'How Much Does TV Mounting Cost? (2026) | BestHand',
    metaDescription:
      'TV mounting costs $100–$300 on average in 2026, depending on TV size, wall type and cable concealment. See the full price breakdown and what is included.',
    h1: 'How much does TV mounting cost?',
    updated: 'June 2026',
    intro:
      'Professional TV mounting typically costs $100–$300 in 2026. The final price depends on your TV size, the wall type (drywall vs. brick or concrete), whether you want the cables hidden, and if a mount needs to be supplied. Here is what goes into the price.',
    costTable: {
      caption: 'Typical TV mounting costs (2026, U.S.)',
      head: ['Service', 'Typical range', 'Notes'],
      rows: [
        ['Basic mount (up to 50")', '$100–$180', 'Drywall, mount supplied by you'],
        ['Large TV (55"–85")', '$150–$300', 'Two-person job for big panels'],
        ['Cable concealment (in-wall)', '+$60–$150', 'Hides cables inside the wall'],
        ['Brick / concrete wall', '+$50–$120', 'Special anchors + drilling'],
        ['Full-motion mount install', '$150–$350', 'Articulating arm, more hardware'],
      ],
    },
    sections: [
      {
        h2: 'What is usually included',
        body: [
          'A standard TV mounting service includes finding the studs, securing the bracket, hanging and leveling the TV, and basic cable tidy-up. Confirm whether the mount itself is included or if you should supply it — a quality fixed mount is $20–$60, a full-motion mount $60–$150.',
        ],
      },
      {
        h2: 'What raises the price',
        body: [
          'Bigger TVs need two installers and heavier hardware. Brick, tile or concrete walls require masonry anchors and add time. In-wall cable concealment is the single most common add-on and gives the cleanest look. A soundbar or extra components add a little labor.',
        ],
      },
    ],
    faqs: [
      ['How much does it cost to mount a TV in 2026?', 'Most TV mounting jobs cost $100–$300, with larger TVs, masonry walls and in-wall cable concealment at the higher end.'],
      ['Does the price include the wall mount?', 'Not always. Ask up front — many pros mount a bracket you supply, while others can provide one for an added cost.'],
      ['How long does TV mounting take?', 'A standard mount takes 45–90 minutes. Cable concealment or masonry walls add time.'],
      ['Can you hide the cables in the wall?', 'Yes, in-wall cable concealment is a popular add-on for $60–$150 and gives the cleanest finish.'],
    ],
    related: ['handyman-cost', 'furniture-assembly-cost'],
  },

  'furniture-assembly-cost': {
    service: 'Furniture Assembly',
    serviceSlug: 'furniture-assembly',
    category: 'Cost guide',
    title: 'How Much Does Furniture Assembly Cost? (2026) | BestHand',
    metaDescription:
      'Furniture assembly costs $50–$200 per item on average in 2026. See typical prices for IKEA furniture, beds, wardrobes and desks, plus how pros charge.',
    h1: 'How much does furniture assembly cost?',
    updated: 'June 2026',
    intro:
      'Professional furniture assembly usually costs $50–$200 per item in 2026, or about $40–$80 per hour. Flat-pack pieces like IKEA wardrobes and bed frames sit at the higher end because of the part count. Here is what to expect.',
    costTable: {
      caption: 'Typical furniture assembly costs (2026, U.S.)',
      head: ['Item', 'Typical range', 'Notes'],
      rows: [
        ['Chair / small table', '$40–$70', 'Quick, single piece'],
        ['Desk / bookshelf', '$60–$120', 'Moderate part count'],
        ['Bed frame', '$70–$150', 'Often two people for large beds'],
        ['Wardrobe / large cabinet', '$120–$250', 'High part count, leveling'],
        ['Hourly rate', '$40–$80 / hour', 'For mixed or multiple items'],
      ],
    },
    sections: [
      {
        h2: 'Per-item vs. hourly',
        body: [
          'For a single known item, a flat per-item price is simplest. If you have several boxes or a whole room of furniture, hourly pricing is usually cheaper. Bundling items into one visit avoids paying multiple trip charges.',
        ],
      },
      {
        h2: 'Tips to keep it affordable',
        body: [
          'Have all boxes, hardware and instructions ready and unboxed in the room where the furniture will live. Flag any items missing parts before the pro arrives, and book multiple pieces together for the best rate.',
        ],
      },
    ],
    faqs: [
      ['How much does it cost to assemble IKEA furniture?', 'IKEA assembly typically costs $50–$250 per item depending on size — a wardrobe or PAX unit is at the top, a small table at the bottom.'],
      ['Do assemblers charge per item or per hour?', 'Both are common. Single items are often flat-rate; multiple items are usually cheaper by the hour.'],
      ['How long does furniture assembly take?', 'Small items take 20–40 minutes; large wardrobes or beds can take 1–2 hours.'],
    ],
    related: ['handyman-cost', 'tv-mounting-cost', 'moving-help-cost'],
  },

  'house-cleaning-cost': {
    service: 'House Cleaning',
    serviceSlug: 'cleaning',
    category: 'Cost guide',
    title: 'How Much Does House Cleaning Cost? (2026 Price Guide) | BestHand',
    metaDescription:
      'House cleaning typically costs around $120–$240 for a standard clean in 2026, or $25–$50 per hour — actual prices vary by home size, condition and location. See deep, move-out and recurring service.',
    h1: 'How much does house cleaning cost?',
    updated: 'June 2026',
    intro:
      'A standard house cleaning costs $120–$240 in 2026, depending on home size and condition, while hourly rates run $25–$50 per cleaner. Deep cleans and move-out cleans cost more because of the extra detail. Here is the full breakdown.',
    costTable: {
      caption: 'Typical house cleaning costs (2026, U.S.)',
      head: ['Type', 'Typical range', 'Notes'],
      rows: [
        ['Standard clean (1–2 bed)', '$120–$180', 'Routine maintenance clean'],
        ['Standard clean (3–4 bed)', '$160–$240', 'Larger homes'],
        ['Deep clean', '$200–$400', 'Baseboards, inside appliances, detail'],
        ['Move-in / move-out', '$250–$500', 'Empty home, top-to-bottom'],
        ['Recurring (per visit)', '$100–$180', 'Discounted vs. one-time'],
      ],
    },
    sections: [
      {
        h2: 'One-time vs. recurring',
        body: [
          'Recurring weekly or biweekly cleans cost less per visit than a one-time clean because the home stays in better condition. A first-time or deep clean is priced higher to bring the home to a baseline.',
        ],
      },
      {
        h2: 'What changes the price',
        body: [
          'Home size, number of bathrooms, pets, clutter and add-ons (inside the oven, inside the fridge, interior windows) all affect the quote. Agreeing on the scope up front keeps the price predictable.',
        ],
      },
    ],
    faqs: [
      ['How much does a house cleaner cost per hour?', 'Most house cleaners charge $25–$50 per hour per cleaner in 2026, with teams completing homes faster.'],
      ['How much is a deep clean?', 'Deep cleaning typically costs $200–$400 because it covers baseboards, inside appliances and detailed scrubbing.'],
      ['Is recurring cleaning cheaper?', 'Yes — weekly or biweekly service usually costs less per visit than a one-time clean.'],
    ],
    related: ['handyman-cost', 'junk-removal-cost'],
  },

  'plumber-cost': {
    service: 'Plumbing',
    serviceSlug: 'plumbing',
    category: 'Cost guide',
    title: 'How Much Does a Plumber Cost? (2026 Price Guide) | BestHand',
    metaDescription:
      'Plumbers charge $45–$200 per hour in 2026, and most common repairs cost $150–$500. See typical prices for leaks, faucets, toilets and drain cleaning.',
    h1: 'How much does a plumber cost?',
    updated: 'June 2026',
    intro:
      'Plumbers typically charge $45–$200 per hour in 2026, and most common household repairs land between $150 and $500. Emergency and after-hours calls cost more. Below are typical prices for the jobs people book most.',
    costTable: {
      caption: 'Typical plumbing costs (2026, U.S.)',
      head: ['Job', 'Typical range', 'Notes'],
      rows: [
        ['Service call / diagnosis', '$75–$150', 'Often credited toward the repair'],
        ['Faucet repair or replace', '$150–$350', 'Plus the fixture'],
        ['Toilet repair / install', '$150–$400', 'Running toilet to full swap'],
        ['Clogged drain clearing', '$130–$350', 'Snake or auger'],
        ['Hourly rate', '$45–$200 / hour', 'Varies widely by region'],
      ],
    },
    sections: [
      {
        h2: 'Why plumbing prices vary',
        body: [
          'Licensing, the difficulty of access, parts, and whether it is an emergency all move the price. After-hours, weekend and holiday calls carry premiums. Always confirm whether the diagnostic fee is credited toward the repair.',
        ],
      },
      {
        h2: 'When to call a pro vs. DIY',
        body: [
          'Minor clogs and a worn washer can be DIY. Anything involving the main line, water heaters, gas, or behind-the-wall pipes should go to a licensed plumber to avoid water damage and code issues.',
        ],
      },
    ],
    faqs: [
      ['How much does a plumber charge per hour?', 'Most plumbers charge $45–$200 per hour in 2026, with higher rates in major metros and for emergencies.'],
      ['How much to fix a leaking faucet?', 'A faucet repair or replacement usually costs $150–$350 plus the cost of the fixture.'],
      ['Is there a call-out fee?', 'Many plumbers charge a $75–$150 service/diagnostic fee, which is often credited toward the repair.'],
    ],
    related: ['electrician-cost', 'handyman-cost'],
  },

  'electrician-cost': {
    service: 'Electrical',
    serviceSlug: 'electrical',
    category: 'Cost guide',
    title: 'How Much Does an Electrician Cost? (2026) | BestHand',
    metaDescription:
      'Electricians charge $50–$130 per hour in 2026, and common jobs cost $150–$600. See typical prices for outlets, fixtures, fans and panel work.',
    h1: 'How much does an electrician cost?',
    updated: 'June 2026',
    intro:
      'Electricians typically charge $50–$130 per hour in 2026, and most common jobs run $150–$600. Because electrical work is licensed and safety-critical, pricing reflects expertise and code compliance. Here is what to expect.',
    costTable: {
      caption: 'Typical electrical costs (2026, U.S.)',
      head: ['Job', 'Typical range', 'Notes'],
      rows: [
        ['Install / replace outlet or switch', '$120–$250', 'Per device'],
        ['Install light fixture', '$150–$350', 'Plus the fixture'],
        ['Install ceiling fan', '$150–$400', 'More if no existing wiring'],
        ['Add a dedicated circuit', '$300–$800', 'Appliance / EV-ready'],
        ['Hourly rate', '$50–$130 / hour', 'Plus possible service fee'],
      ],
    },
    sections: [
      {
        h2: 'What drives the price',
        body: [
          'Accessibility (finished walls vs. open framing), the age of your wiring, permit requirements and the fixture itself all matter. Anything touching the panel or adding circuits costs more and should always be done by a licensed electrician.',
        ],
      },
    ],
    faqs: [
      ['How much does an electrician cost per hour?', 'Most electricians charge $50–$130 per hour in 2026, plus a possible service call fee.'],
      ['How much to install a ceiling fan?', 'Ceiling fan installation typically costs $150–$400, more if new wiring or a support box is needed.'],
      ['Do I need a licensed electrician?', 'Yes for anything beyond swapping a fixture — outlets, circuits and panel work should be done by a licensed pro for safety and code.'],
    ],
    related: ['plumber-cost', 'handyman-cost'],
  },

  'lawn-care-cost': {
    service: 'Lawn Care',
    serviceSlug: 'lawn-care',
    category: 'Cost guide',
    title: 'How Much Does Lawn Care Cost? (2026 Price Guide) | BestHand',
    metaDescription:
      'Lawn mowing costs $30–$80 per visit in 2026, or $100–$400 per month for full service. See prices for mowing, edging, cleanups and seasonal care.',
    h1: 'How much does lawn care cost?',
    updated: 'June 2026',
    intro:
      'A single lawn mowing visit typically costs $30–$80 in 2026, while full-service monthly plans run $100–$400 depending on yard size and frequency. Seasonal jobs like leaf cleanup are priced separately. Here is the breakdown.',
    costTable: {
      caption: 'Typical lawn care costs (2026, U.S.)',
      head: ['Service', 'Typical range', 'Notes'],
      rows: [
        ['Mowing (per visit)', '$30–$80', 'Small to average yard'],
        ['Mow + edge + trim', '$50–$120', 'Full tidy-up'],
        ['Monthly plan', '$100–$400', 'Weekly / biweekly service'],
        ['Leaf / yard cleanup', '$150–$500', 'Seasonal, by volume'],
        ['Hedge / shrub trimming', '$60–$250', 'Depends on size and count'],
      ],
    },
    sections: [
      {
        h2: 'What affects the quote',
        body: [
          'Yard size, terrain, how overgrown it is, and how often you book all change the price. Recurring weekly or biweekly service is cheaper per visit than one-off cuts because the lawn stays manageable.',
        ],
      },
    ],
    faqs: [
      ['How much does it cost to mow a lawn?', 'A typical mowing visit costs $30–$80, with larger or overgrown yards at the higher end.'],
      ['Is a monthly lawn plan cheaper?', 'Yes — recurring service usually costs less per visit than booking one-time cuts.'],
    ],
    related: ['junk-removal-cost', 'handyman-cost'],
  },

  'junk-removal-cost': {
    service: 'Junk Removal',
    serviceSlug: 'junk-removal',
    category: 'Cost guide',
    title: 'How Much Does Junk Removal Cost? (2026) | BestHand',
    metaDescription:
      'Junk removal costs $100–$600 in 2026, priced mainly by volume. See typical prices for single items, half loads and full truck loads, plus what affects cost.',
    h1: 'How much does junk removal cost?',
    updated: 'June 2026',
    intro:
      'Junk removal is usually priced by how much space your items take in the truck, with most jobs costing $100–$600 in 2026. Single-item pickups are cheaper; full truckloads cost the most. Here is what to expect.',
    costTable: {
      caption: 'Typical junk removal costs (2026, U.S.)',
      head: ['Load size', 'Typical range', 'Notes'],
      rows: [
        ['Single item', '$75–$150', 'One couch, mattress, appliance'],
        ['1/4 truck load', '$150–$250', 'A few items'],
        ['1/2 truck load', '$300–$450', 'Room clear-out'],
        ['Full truck load', '$450–$600+', 'Garage / whole-home declutter'],
      ],
    },
    sections: [
      {
        h2: 'What affects the price',
        body: [
          'Volume is the main factor, but weight (concrete, dirt), stairs, and special-disposal items (mattresses, electronics, appliances with refrigerant) can add fees. Clear access and ground-floor pickup keep costs down.',
        ],
      },
    ],
    faqs: [
      ['How is junk removal priced?', 'Mostly by volume — how much of the truck your items fill. Single items are cheapest; full loads cost the most.'],
      ['How much to remove a couch or mattress?', 'A single large item is typically $75–$150 to haul away.'],
    ],
    related: ['house-cleaning-cost', 'moving-help-cost'],
  },

  'interior-painting-cost': {
    service: 'Painting',
    serviceSlug: 'painting',
    category: 'Cost guide',
    title: 'How Much Does Interior Painting Cost? (2026) | BestHand',
    metaDescription:
      'Interior painting typically costs around $2–$6 per square foot in 2026, or $300–$800 per room — actual prices vary by job. See typical prices by room and what affects the cost of a paint job.',
    h1: 'How much does interior painting cost?',
    updated: 'June 2026',
    intro:
      'Interior painting typically costs $2–$6 per square foot in 2026, which works out to about $300–$800 per average room including labor and basic prep. Ceilings, trim and color changes add to the total. Here is the breakdown.',
    costTable: {
      caption: 'Typical interior painting costs (2026, U.S.)',
      head: ['Space', 'Typical range', 'Notes'],
      rows: [
        ['Average bedroom', '$300–$700', 'Walls only'],
        ['Living room', '$500–$1,200', 'Larger, higher walls'],
        ['Walls + ceiling + trim', '+30–50%', 'Full room refresh'],
        ['Per square foot', '$2–$6 / sq ft', 'Industry rule of thumb'],
      ],
    },
    sections: [
      {
        h2: 'What changes the price',
        body: [
          'Wall height, prep work (patching, sanding, priming), the number of colors, and paint quality all affect the quote. Dark-to-light color changes need extra coats. Clearing the room and removing fixtures yourself can lower labor time.',
        ],
      },
    ],
    faqs: [
      ['How much does it cost to paint a room?', 'A typical bedroom costs $300–$700 for walls; adding ceiling and trim raises it 30–50%.'],
      ['How is painting priced?', 'Often by the square foot ($2–$6) or as a flat per-room price after the pro sees the space.'],
    ],
    related: ['handyman-cost', 'house-cleaning-cost'],
  },

  'moving-help-cost': {
    service: 'Moving Help',
    serviceSlug: 'moving',
    category: 'Cost guide',
    title: 'How Much Does Moving Help Cost? (2026) | BestHand',
    metaDescription:
      'Moving labor costs $40–$80 per mover per hour in 2026, and a typical local move runs $300–$700. See prices for loading, unloading and in-home moves.',
    h1: 'How much does moving help cost?',
    updated: 'June 2026',
    intro:
      'Hourly moving labor (loading, unloading or rearranging) typically costs $40–$80 per mover per hour in 2026, and a typical local move with a couple of helpers runs $300–$700. Here is how moving help is priced.',
    costTable: {
      caption: 'Typical moving help costs (2026, U.S.)',
      head: ['Service', 'Typical range', 'Notes'],
      rows: [
        ['Labor (per mover)', '$40–$80 / hour', '2-hour minimum common'],
        ['Load or unload only', '$150–$400', 'You provide the truck'],
        ['Local move (2 movers)', '$300–$700', 'Apartment / small home'],
        ['In-home / heavy item move', '$80–$200', 'Move furniture within a home'],
      ],
    },
    sections: [
      {
        h2: 'How movers charge',
        body: [
          'Most labor-only moving help is billed hourly per mover, with a 2-hour minimum. Stairs, long carries, very heavy items and last-minute bookings can add to the cost. Being fully packed and ready when the movers arrive keeps the bill down.',
        ],
      },
    ],
    faqs: [
      ['How much do movers charge per hour?', 'Labor-only movers typically charge $40–$80 per mover per hour, with a 2-hour minimum.'],
      ['How much for a local move?', 'A typical local move with two helpers runs $300–$700 depending on size and distance.'],
    ],
    related: ['furniture-assembly-cost', 'junk-removal-cost'],
  },

  'how-to-hire-a-handyman': {
    service: 'Handyman',
    serviceSlug: 'handyman',
    category: 'How-to guide',
    title: 'How to Hire a Trustworthy Handyman (2026 Checklist) | BestHand',
    metaDescription:
      'How to hire a reliable handyman: what to check, questions to ask, red flags to avoid, and how to get a fair price. A practical 2026 hiring checklist.',
    h1: 'How to hire a trustworthy handyman',
    updated: 'June 2026',
    intro:
      'Hiring the right handyman saves money and headaches. The goal is simple: a vetted, well-reviewed pro who shows up, quotes fairly and does it right the first time. Use this checklist before you book.',
    sections: [
      {
        h2: '1. Verify the pro',
        body: [
          // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
          'Look for a background check and recent, real reviews. On BestHand every pro is background-checked before they can take a job, and rated by customers after each one.',
        ],
      },
      {
        h2: '2. Be specific about the job',
        body: [
          'List every task with photos and measurements. A clear scope gets you an accurate, upfront price instead of a vague hourly estimate that balloons. Bundle small jobs into one visit to save on trip charges.',
        ],
      },
      {
        h2: '3. Confirm pricing and payment up front',
        body: [
          'Agree on the price and what is included before work starts, and prefer secure in-app payment over cash so you have a receipt and recourse. Avoid large upfront deposits for small jobs.',
        ],
      },
      {
        h2: '4. Watch for red flags',
        body: [
          'Be cautious of no reviews, pressure to pay cash up front, no clear scope, or a quote far below everyone else. The cheapest unvetted option is often the most expensive once you pay for a redo.',
        ],
      },
    ],
    faqs: [
      ['What should I check before hiring a handyman?', 'A background check, recent reviews, a clear written scope, and upfront pricing with secure payment.'],
      ['How do I get a fair handyman price?', 'Describe the job clearly with photos, bundle tasks into one visit, and compare upfront prices from vetted pros rather than vague hourly estimates.'],
      ['Should I pay a handyman in cash up front?', 'Avoid large upfront cash deposits for small jobs. Secure in-app payment when the work is done protects you and gives you a receipt.'],
    ],
    related: ['handyman-cost', 'tv-mounting-cost'],
  },
};

Object.assign(GUIDE_PAGES, HOME_CARE_PAGES);

export const GUIDE_SLUGS = Object.keys(GUIDE_PAGES);

// Ordered list for the /guides index: Home care first (the pillar we lead with),
// then cost guides, then how-to/hiring.
const CATEGORY_ORDER = { 'Home care': 0, 'Cost guide': 1 };
export const GUIDE_LIST = GUIDE_SLUGS.map((slug) => ({
  slug,
  h1: GUIDE_PAGES[slug].h1,
  category: GUIDE_PAGES[slug].category,
  service: GUIDE_PAGES[slug].service,
  metaDescription: GUIDE_PAGES[slug].metaDescription,
  readTime: GUIDE_PAGES[slug].readTime,
  featured: !!GUIDE_PAGES[slug].featured,
})).sort((a, b) => {
  const ca = CATEGORY_ORDER[a.category] != null ? CATEGORY_ORDER[a.category] : 2;
  const cb = CATEGORY_ORDER[b.category] != null ? CATEGORY_ORDER[b.category] : 2;
  return ca - cb;
});

export const GUIDES_CTA = CTA;
